import { useEffect, useState } from "react";
import {
  formatPesoWithVat,
  SMS_CREDIT_APPLICATION_TTL_HOURS,
  VAT_RATE,
} from "@orbit/api-zod";
import { merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

interface PackageWire {
  sku: string;
  credits: number;
  pesoAmount: number;
}

interface CreditsBalance {
  balance: number;
  lifetimePurchased: number;
  lifetimeConsumed: number;
}

interface ApplicationView {
  id: string;
  packageSku: string;
  credits: number;
  pesoAmount: number;
  status: "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";
  paymentReference: string | null;
  adminNotes: string | null;
  decidedAt: string | null;
  createdAt: string;
}

const STATUS_STYLES: Record<ApplicationView["status"], string> = {
  PENDING: "bg-amber-100 text-amber-800",
  APPROVED: "bg-emerald-100 text-emerald-800",
  REJECTED: "bg-red-100 text-red-800",
  CANCELLED: "bg-gray-200 text-gray-700",
};

export function MerchantSmsCreditsPage() {
  const [balance, setBalance] = useState<CreditsBalance | null>(null);
  const [packages, setPackages] = useState<PackageWire[]>([]);
  const [applications, setApplications] = useState<ApplicationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  async function loadAll() {
    setErr(null);
    const [b, p, a] = await Promise.all([
      merchantFetch<CreditsBalance>("/api/v1/tenant/me/sms-credits"),
      merchantFetch<{ packages: PackageWire[] }>(
        "/api/v1/tenant/me/sms-credits/packages",
      ),
      merchantFetch<{ applications: ApplicationView[] }>(
        "/api/v1/tenant/me/sms-credits/applications",
      ),
    ]);
    if (b.ok) setBalance(b.data);
    if (p.ok) setPackages(p.data.packages);
    if (a.ok) setApplications(a.data.applications);
    if (!b.ok) setErr(b.message);
    else if (!p.ok) setErr(p.message);
    else if (!a.ok) setErr(a.message);
    setLoading(false);
  }

  useEffect(() => {
    void loadAll();
  }, []);

  async function apply(sku: string) {
    setFlash(null);
    setErr(null);
    setSubmitting(sku);
    const r = await merchantFetch<{ application: ApplicationView }>(
      "/api/v1/tenant/me/sms-credits/applications",
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ packageSku: sku }),
      },
    );
    setSubmitting(null);
    if (!r.ok) {
      setErr(r.message);
      return;
    }
    setFlash(
      `Application submitted. Settle payment within ${SMS_CREDIT_APPLICATION_TTL_HOURS} hours — credits are added automatically once we confirm receipt; otherwise the application auto-cancels.`,
    );
    await loadAll();
  }

  async function cancelApplication(id: string) {
    if (
      !window.confirm(
        "Cancel this pending application? You can re-apply for the same package afterwards.",
      )
    ) {
      return;
    }
    setFlash(null);
    setErr(null);
    setCancelling(id);
    const r = await merchantFetch<{ application: ApplicationView }>(
      `/api/v1/tenant/me/sms-credits/applications/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
    setCancelling(null);
    if (!r.ok) {
      setErr(r.message);
      return;
    }
    setFlash("Application cancelled.");
    await loadAll();
  }

  return (
    <div className="min-h-screen bg-cream">
      <MerchantNav current="sms-credits" />
      <main className="mx-auto max-w-4xl px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy">SMS credits</h1>
          <p className="mt-1 text-sm text-ink-dark">
            SMS credits fund SMS broadcasts from your Inbox. One credit
            covers one SMS recipient. Apply for a package below; once we
            confirm payment, the credits are added to your balance
            automatically.
          </p>
        </div>

        {loading && <div className="text-sm text-ink-dark">Loading…</div>}

        {err && (
          <div
            className="rounded-md bg-red-50 p-3 text-sm text-red-800"
            data-testid="banner-error"
          >
            {err}
          </div>
        )}
        {flash && (
          <div
            className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-800"
            data-testid="banner-flash"
          >
            {flash}
          </div>
        )}

        {balance && (
          <section className="rounded-lg border border-amber-light bg-white p-4">
            <h2 className="text-base font-semibold text-navy">Your balance</h2>
            <dl className="mt-3 grid grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="text-ink-dark">Available</dt>
                <dd
                  className="text-2xl font-semibold text-navy"
                  data-testid="text-credits-balance"
                >
                  {balance.balance.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-ink-dark">Lifetime purchased</dt>
                <dd className="text-xl text-navy">
                  {balance.lifetimePurchased.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-ink-dark">Lifetime sent</dt>
                <dd className="text-xl text-navy">
                  {balance.lifetimeConsumed.toLocaleString()}
                </dd>
              </div>
            </dl>
          </section>
        )}

        <section className="rounded-lg border border-amber-light bg-white p-4">
          <h2 className="text-base font-semibold text-navy">Packages</h2>
          <p className="mt-1 text-xs text-ink-dark">
            Choose a package, then send your payment via the channel
            agreed with the Orbit team. Your credits will appear here
            once we confirm receipt.{" "}
            <span className="font-medium text-navy">
              Prices shown exclude {Math.round(VAT_RATE * 100)}% VAT, which
              will be added to your final invoice. Pending applications
              auto-cancel if payment is not settled within{" "}
              {SMS_CREDIT_APPLICATION_TTL_HOURS} hours.
            </span>
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {packages.map((p) => {
              const perCredit = p.pesoAmount / p.credits;
              return (
                <div
                  key={p.sku}
                  className="flex flex-col rounded-md border border-amber-light p-4"
                  data-testid={`card-package-${p.sku}`}
                >
                  <div className="text-3xl font-semibold leading-none text-navy">
                    {p.credits.toLocaleString()}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-ink-dark">
                    SMS credits
                  </div>
                  <div className="mt-3 text-lg font-semibold leading-tight text-navy">
                    ₱{p.pesoAmount.toLocaleString()}
                  </div>
                  <div className="mt-0.5 text-xs text-ink-dark">
                    ≈ ₱{perCredit.toFixed(2)} / credit
                  </div>
                  <div className="mt-0.5 text-xs italic text-ink-dark">
                    + {Math.round(VAT_RATE * 100)}% VAT ={" "}
                    <span className="font-semibold not-italic text-navy">
                      {formatPesoWithVat(p.pesoAmount)}
                    </span>
                  </div>
                  <button
                    onClick={() => void apply(p.sku)}
                    disabled={submitting !== null}
                    className="mt-auto w-full rounded-md bg-navy px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                    data-testid={`button-apply-${p.sku}`}
                  >
                    {submitting === p.sku ? "Submitting…" : "Apply"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-lg border border-amber-light bg-white p-4">
          <h2 className="text-base font-semibold text-navy">
            Your applications
          </h2>
          {applications.length === 0 ? (
            <p className="mt-3 text-sm text-ink-dark">
              No applications yet. Pick a package above to get started.
            </p>
          ) : (
            <table className="mt-3 w-full text-sm">
              <thead>
                <tr className="border-b border-amber-light text-left text-xs text-ink-dark">
                  <th className="py-1 pr-3">Submitted</th>
                  <th className="py-1 pr-3">Package</th>
                  <th className="py-1 pr-3 text-right">
                    Amount due
                    <span className="block text-[10px] font-normal normal-case text-ink-dark">
                      (incl. {Math.round(VAT_RATE * 100)}% VAT)
                    </span>
                  </th>
                  <th className="py-1 pr-3">Status</th>
                  <th className="py-1 pr-3">Decided</th>
                  <th className="py-1 pr-3">Notes</th>
                  <th className="py-1 pr-3" />
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-amber-light/40 align-top"
                    data-testid={`row-application-${a.id}`}
                  >
                    <td className="py-1 pr-3 text-ink-dark">
                      {new Date(a.createdAt).toLocaleString()}
                    </td>
                    <td className="py-1 pr-3 text-navy">
                      {a.credits.toLocaleString()} credits
                    </td>
                    <td className="py-1 pr-3 text-right text-navy">
                      <div className="font-semibold">
                        {formatPesoWithVat(a.pesoAmount)}
                      </div>
                      <div className="text-[10px] text-ink-dark">
                        base ₱{a.pesoAmount.toLocaleString()}
                      </div>
                    </td>
                    <td className="py-1 pr-3">
                      <span
                        className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[a.status]}`}
                      >
                        {a.status}
                      </span>
                    </td>
                    <td className="py-1 pr-3 text-ink-dark">
                      {a.decidedAt
                        ? new Date(a.decidedAt).toLocaleString()
                        : "—"}
                    </td>
                    <td className="py-1 pr-3 text-ink-dark">
                      {a.adminNotes ?? a.paymentReference ?? "—"}
                    </td>
                    <td className="py-1 pr-3 text-right">
                      {a.status === "PENDING" && (
                        <button
                          onClick={() => void cancelApplication(a.id)}
                          disabled={cancelling !== null}
                          className="rounded-md border border-ink-mid bg-white px-2 py-1 text-xs font-medium text-navy hover:bg-amber-light/30 disabled:opacity-50"
                          data-testid={`button-cancel-${a.id}`}
                        >
                          {cancelling === a.id ? "Cancelling…" : "Cancel"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
}
